// Auto-focused barcode input. On Enter:
//   - If a serialResolver is provided: tries /v1/scan first (serial-first for IMEI).
//     kind=serial → onSerialAdd callback; kind=variant → falls through to onAdd/onMultiple.
//   - Otherwise (no resolver): falls back to /v1/variants/search as before.
import { useRef, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { apiGet } from "@/lib/api/client";
import type { ScanResult, ScanSerialCandidate } from "@/features/inventory/use-scan-resolve";
import { SerialPickModal } from "@/features/inventory/serial-pick-modal";
import { useScannerAutoSubmit } from "@/features/inventory/use-scanner-auto-submit";

interface VariantResult {
  id: string;
  sku: string;
  barcode?: string | null;
  productId: string;
  productName: string;
  sellPrice: string;
  costPrice: string;
  imageUrls?: string[];
}

interface Props {
  onAdd: (variantId: string, imageUrl?: string) => void;
  onMultiple: (results: VariantResult[]) => void;
  disabled?: boolean;
  /**
   * When provided, the barcode input runs serial-first scan via this resolver
   * before falling back to variant search. The callback is called when a
   * kind=serial & ok=true result is obtained.
   */
  serialResolver?: (q: string) => Promise<ScanResult>;
  onSerialAdd?: (variantId: string, serialId: string) => void;
  onSerialError?: (reason: string) => void;
}

export function BarcodeInput({
  onAdd,
  onMultiple,
  disabled,
  serialResolver,
  onSerialAdd,
  onSerialError,
}: Props) {
  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  // Alias trúng nhiều máy → modal chọn thủ công (kèm chuỗi đã gõ để đối chiếu).
  const [serialChoices, setSerialChoices] = useState<{ q: string; list: ScanSerialCandidate[] } | null>(null);

  // Restore focus when component mounts or after each action.
  useEffect(() => {
    const id = setTimeout(() => ref.current?.focus(), 50);
    return () => clearTimeout(id);
  }, [disabled]);

  // Runs the scan/search for the current value. Triggered by Enter (hardware
  // scanners) or the "Thêm" button / keyboard "Go" key (mobile, no Enter).
  const submit = async () => {
    const q = value.trim();
    if (!q || busy) return;
    setBusy(true);
    try {
      // Serial-first path: only when a resolver and its callback are wired in.
      if (serialResolver && onSerialAdd) {
        const sr = await serialResolver(q);
        if (sr.kind === "serial") {
          setValue("");
          if (sr.ok) {
            onSerialAdd(sr.variant.id, sr.serial.id);
          } else {
            onSerialError?.(sr.reason ?? "Serial không hợp lệ");
          }
          return;
        }
        // Nhiều máy còn tồn trùng đuôi số → nhân viên tự chọn từ danh sách.
        if (sr.kind === "serials") {
          setValue("");
          setSerialChoices({ q, list: sr.serials });
          return;
        }
        // kind === "none" → still try variant search below (may be SKU/barcode)
        if (sr.kind === "none") {
          // fall through to variant search
        }
        // kind === "variant" → already have matches from scan, use them
        if (sr.kind === "variant") {
          setValue("");
          if (sr.matches.length === 0) return;
          if (sr.matches.length === 1) {
            onAdd(sr.matches[0].id);
          } else {
            // Remap to VariantResult shape expected by onMultiple
            onMultiple(
              sr.matches.map((m) => ({
                id: m.id,
                sku: m.sku,
                productId: "",
                productName: m.productName,
                sellPrice: m.sellPrice,
                costPrice: "0",
                barcode: m.barcode ?? null,
              })),
            );
          }
          return;
        }
      }

      // Variant-search fallback (non-serial items or no resolver).
      const results = await apiGet<VariantResult[]>("/v1/variants/search", { q, limit: 5 });
      setValue("");
      if (!results || results.length === 0) return;
      if (results.length === 1) {
        onAdd(results[0].id, results[0].imageUrls?.[0]);
      } else {
        onMultiple(results);
      }
    } catch {
      // Silently ignore — cashier can try again. Specific serial errors surfaced via onSerialError.
    } finally {
      setBusy(false);
      ref.current?.focus();
    }
  };

  // Máy quét không gửi Enter → tự chốt khi vừa tít xong (Enter vẫn dùng được).
  useScannerAutoSubmit(value, () => void submit(), { enabled: !disabled && !busy });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    void submit();
  };

  return (
    <div className="flex gap-2">
      <Input
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        enterKeyHint="search"
        placeholder="Quét / gõ IMEI, barcode hoặc SKU… (quét xong tự thêm)"
        disabled={disabled || busy}
        className="font-mono flex-1"
        autoComplete="off"
        data-testid="barcode-input"
      />
      <Button
        type="button"
        onClick={() => void submit()}
        disabled={disabled || busy || !value.trim()}
        className="shrink-0"
      >
        Thêm
      </Button>
      {serialChoices && onSerialAdd && (
        <SerialPickModal
          candidates={serialChoices.list}
          query={serialChoices.q}
          onPick={(c) => {
            setSerialChoices(null);
            onSerialAdd(c.variant.id, c.serial.id);
            ref.current?.focus();
          }}
          onClose={() => {
            setSerialChoices(null);
            ref.current?.focus();
          }}
        />
      )}
    </div>
  );
}
